import React, { useState, useEffect, useCallback } from 'react';
import MetaTags from "react-meta-tags";
import ApiClient from '../../BaseUrl/ApiClient';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faPlus, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useParams, useHistory } from 'react-router-dom';

/* debounce helper */
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

function DailySpecial() {
  const { businessId } = useParams();
  const history = useHistory();

  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: 10
  });
  const [searchQuery, setSearchQuery] = useState('');

  const [deleteModal, setDeleteModal] = useState(false);
  const [dealToDelete, setDealToDelete] = useState(null);

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const fetchDeals = async (page = 1, limit = pagination.limit, searchParam = searchQuery) => {
    if (!businessId) return;

    setIsLoading(true);
    try {
      const params = {
        page: page.toString(),
        limit: limit.toString(),
      };
      if (searchParam) params.searchParam = searchParam;

      const response = await ApiClient().get(
        `/admin/deal/dailySpecials/${businessId}/list`,
        { params }
      );

      const { responseData } = response.data;
      setDeals(responseData.deals || []);
      setPagination(responseData.pagination);
    } catch (error) {
      toast.error(
        error.response?.data?.responseMessage?.[0] || "Failed to fetch deals"
      );
    } finally {
      setIsLoading(false);
    }
  };


  const debouncedFetchDeals = useCallback(
    debounce((page, limit, searchParam) => fetchDeals(page, limit, searchParam), 500),
    [pagination.limit, businessId]
  );

  useEffect(() => {
    fetchDeals();
  }, [businessId]);

  const handleSearchInputChange = (e) => {
    const searchParam = e.target.value;
    setSearchQuery(searchParam);
    debouncedFetchDeals(1, pagination.limit, searchParam);
  };


  const confirmDelete = (deal) => {
    setDealToDelete(deal);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    if (!dealToDelete) return;

    try {
      await ApiClient().delete(`/admin/deal/dailySpecials/${dealToDelete._id}`);
      toast.success("Deal deleted successfully");
      fetchDeals();
    } catch (error) {
      toast.error(
        error.response?.data?.responseMessage?.[0] || "Failed to delete deal"
      );
    } finally {
      setDealToDelete(null);
      toggleDeleteModal();
    }
  };

  const handleUpdate = (dealId) => {
    history.push(`/update-daily-special/${dealId}`);
  };

  const toggleActiveStatus = async (deal) => {
    const newStatus = deal.isActive ? "false" : "true"; // toggle
    try {
      await ApiClient().patch(`/admin/deal/dailySpecials/${deal._id}/${newStatus}`);
      toast.success(`Deal ${deal.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchDeals();
    } catch (error) {
      toast.error(
        error.response?.data?.responseMessage?.[0] || "Failed to update status"
      );
    }
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <MetaTags>
        <title>Daily Specials</title>
      </MetaTags>

      <Container fluid className="mt-3">
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h2 className="card-title mb-0">Daily Special Deals</h2>

                <Button
                  color="primary"
                  onClick={() => history.push(`/add-daily-special/${businessId}`)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Daily Special
                </Button>
              </CardHeader>

              <CardBody>
                <Row className="justify-content-end mb-3">
                  <Col md={4}>
                    <FormGroup className="position-relative">
                      <Input
                        type="text"
                        placeholder="Search deals..."
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        className="ps-5"
                      />
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="position-absolute"
                        style={{ top: '10px', left: '10px' }}
                      />
                      {searchQuery && (
                        <FontAwesomeIcon
                          icon={faTimes}
                          className="position-absolute"
                          style={{ top: '10px', right: '10px', cursor: 'pointer' }}
                          onClick={() => {
                            setSearchQuery('');
                            fetchDeals(1, pagination.limit, '');
                          }}

                        />
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <div className="table-responsive">
                  <table className="table table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Deal Photo</th>
                        <th>Price</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan="6" className="text-center">Loading...</td>
                        </tr>
                      ) : deals.length ? (
                        deals.map(deal => (
                          <tr key={deal._id}>
                            <td>{deal.name}</td>
                            <td>
                              {deal.dealPhoto?.url ? (
                                <img
                                  src={deal.dealPhoto.url}
                                  alt="deal"
                                  width="50"
                                  height="50"
                                  style={{ objectFit: 'cover' }}
                                />
                              ) : 'No Photo'}
                            </td>
                            <td>${deal.price}</td>
                            <td>{deal.category?.name || '-'}</td>
                            <td>
                              <span
                                className={`badge ${deal.isActive ? 'bg-success' : 'bg-danger'}`}
                                style={{ padding: '0.4em 0.8em', borderRadius: '50px' }}
                              >
                                {deal.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <Button
                                size="sm"
                                className="me-2"
                                onClick={() => history.push(`/daily-special-detail/${deal._id}`)}
                              >
                                View
                              </Button>
                              <Button
                                color="primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleUpdate(deal._id)}
                              >
                                <FontAwesomeIcon icon={faPencil} />
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                className="me-2"
                                onClick={() => confirmDelete(deal)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                              <Button
                                color={deal.isActive ? "warning" : "success"}
                                size="sm"
                                className="me-2"
                                onClick={() => toggleActiveStatus(deal)}
                              >
                                {deal.isActive ? "Deactivate Deal" : "Activate Deal"}
                              </Button>

                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">No deals found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
          <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
          <ModalBody>
            Are you sure you want to delete <strong>{dealToDelete?.name}</strong>?
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={handleDelete}>Delete</Button>
            <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
          </ModalFooter>
        </Modal>
      </Container>
    </React.Fragment>
  );
}

export default DailySpecial;
